import React, { createContext, useState, useContext, useEffect, useMemo, ReactNode } from 'react';
import { de } from '../translations/de';
import { en } from '../translations/en';
import { es } from '../translations/es';
import { ru } from '../translations/ru';

type Language = 'de' | 'en' | 'es' | 'ru';
type Translations = Record<string, any>;

interface LanguageContextType {
  language: Language;
  translations: Translations;
  setLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Check if a language preference exists in localStorage
  const getInitialLanguage = (): Language => {
    try {
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage === 'en' || savedLanguage === 'de' || savedLanguage === 'es' || savedLanguage === 'ru') {
        return savedLanguage as Language;
      }
      
      // Fallback to browser language if available
      const browserLang = navigator.language.split('-')[0];
      if (browserLang === 'en' || browserLang === 'de' || browserLang === 'es' || browserLang === 'ru') {
        return browserLang as Language;
      }
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
    }
    
    // Default to Spanish based on location in Mallorca
    return 'es';
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);
  
  // Get the translations based on selected language with useMemo to recalculate when language changes
  const translations = useMemo(() => {
    switch(language) {
      case 'en':
        return en;
      case 'es':
        return es;
      case 'ru':
        return ru;
      case 'de':
      default:
        return de;
    }
  }, [language]);

  const setLanguage = (newLanguage: Language) => {
    try {
      localStorage.setItem('language', newLanguage);
    } catch (error) {
      console.warn('Error writing to localStorage:', error);
    }
    
    setLanguageState(newLanguage);
    document.documentElement.lang = newLanguage;
    
    // Force a re-render with the new language
    console.log(`Language changed to: ${newLanguage}`);
  };

  // Set the html lang attribute on mount and when language changes
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, translations, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 